import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Film, Lightbulb, MousePointer2, Search, FileText, ArrowRight, Play, Sparkles } from 'lucide-react';
export function CheStructureScreen() {
  const steps = [{
    icon: Film,
    title: 'Animation',
    subtitle: "Does your skin feel like it's fighting against you?",
    bg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    isPlaceholder: true
  }, {
    icon: Lightbulb,
    title: 'Recognition',
    subtitle: 'Atopic dermatitis affects up to 10% of adults',
    image: 'https://cdn.magicpatterns.com/uploads/ucik6hndSjoZdRyV1ohSTH/Screenshot_2026-04-15_at_19.07.20.png',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-600'
  }, {
    icon: MousePointer2,
    title: 'Quiz',
    subtitle: 'Which of these triggers affect you most?',
    image: 'https://cdn.magicpatterns.com/uploads/nEa1DRvQAYucUq43JHRZzk/Screenshot_2026-04-15_at_19.07.28.png',
    bg: 'bg-slate-100',
    iconColor: 'text-slate-700'
  }, {
    icon: Search,
    title: 'Explore',
    subtitle: 'Understanding your symptoms and triggers',
    image: 'https://cdn.magicpatterns.com/uploads/m5peqBmvi2XAvZ7dCAA5qN/Screenshot_2026-04-15_at_19.07.54.png',
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-600'
  }];
  return <section className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 py-20" data-id="element-98">
      <div className="max-w-7xl w-full mx-auto flex flex-col items-center" data-id="element-99">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-5xl font-bold text-slate-900 text-center mb-20" data-id="element-100">
          Progressive engagement in action
        </motion.h2>

        <div className="flex flex-col lg:flex-row items-center justify-center w-full gap-4 lg:gap-2 mb-12" data-id="element-101">
          {steps.map((step, index) => <Fragment key={index} data-id="element-102">
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15,
            duration: 0.5
          }} className={`w-full lg:w-1/4 ${step.bg} text-slate-900 p-6 rounded-2xl flex flex-col shadow-sm relative group transition-transform hover:-translate-y-2 min-h-[300px]`} data-id="element-103">
                <div className="flex items-center gap-3 mb-4" data-id="element-104">
                  <step.icon size={24} className={step.iconColor} data-id="element-105" />
                  <h3 className="font-bold text-lg" data-id="element-106">{step.title}</h3>
                </div>

                <p className="font-medium text-sm mb-6 flex-grow text-slate-600" data-id="element-107">
                  "{step.subtitle}"
                </p>

                {step.isPlaceholder ? <div className="w-full h-32 rounded-lg bg-slate-900 overflow-hidden shadow-sm mt-auto relative flex items-center justify-center" data-id="element-108">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 to-slate-900" data-id="element-109" />
                    <motion.div animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut'
              }} className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center z-10 shadow-lg shadow-purple-500/30" data-id="element-110">
                      <Play size={20} className="text-white ml-1" fill="white" data-id="element-111" />
                    </motion.div>
                    <div className="absolute bottom-2 left-0 right-0 text-center z-10" data-id="element-112">
                      <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider" data-id="element-113">
                        Animated Intro
                      </span>
                    </div>
                  </div> : step.image ? <div className="w-full h-32 rounded-lg overflow-hidden border border-black/5 shadow-sm mt-auto relative" data-id="element-114">
                    <img src={step.image} alt={step.title} className="w-full h-full object-cover object-top" data-id="element-115" />
                  </div> : null}
              </motion.div>

              {index < steps.length - 1 && <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15 + 0.1
          }} className="hidden lg:flex text-slate-300 shrink-0" data-id="element-116">
                  <ArrowRight size={20} data-id="element-117" />
                </motion.div>}
            </Fragment>)}
        </div>

        {/* Arrow down to CTA */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8
      }} className="flex justify-center mb-12" data-id="element-118">
          <ArrowRight size={28} className="text-purple-400 rotate-90" data-id="element-119" />
        </motion.div>

        {/* Discussion Guide as the CTA Snak */}
        <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1,
        duration: 0.6
      }} className="w-full max-w-5xl" data-id="element-120">
          <div className="relative" data-id="element-121">
            {/* Glow effect */}
            <div className="absolute -inset-3 bg-gradient-to-br from-purple-200 via-purple-100 to-emerald-100 rounded-3xl blur-xl opacity-70" data-id="element-122" />

            <div className="relative bg-white rounded-3xl shadow-lg border-2 border-purple-200 overflow-hidden" data-id="element-123">
              <div className="flex flex-col md:flex-row" data-id="element-124">
                {/* Left: Content */}
                <div className="flex-1 p-8 md:p-12 flex flex-col justify-center" data-id="element-125">
                  <div className="flex items-center gap-3 mb-4" data-id="element-126">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-127">
                      <Sparkles size={20} className="text-purple-600" data-id="element-128" />
                    </div>
                    <span className="text-purple-600 font-bold text-sm uppercase tracking-wider" data-id="element-129">
                      The CTA — Made into a Snak
                    </span>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-130">
                    AD Discussion Guide
                  </h3>

                  <p className="text-slate-600 mb-6 text-base leading-relaxed" data-id="element-131">
                    The static "Prepare for your visit" guide becomes an
                    interactive Snak — walking patients through discussion
                    points, helping them prepare questions, and building
                    confidence before their appointment.
                  </p>

                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-132">
                    <span className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold" data-id="element-133">
                      Interactive
                    </span>
                    <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold" data-id="element-134">
                      Personalized
                    </span>
                    <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold" data-id="element-135">
                      Standalone Snak
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-136">
                    <FileText size={16} data-id="element-137" />
                    <span data-id="element-138">Static PDF → Interactive experience</span>
                  </div>
                </div>

                {/* Right: Screenshot */}
                <div className="flex-1 relative min-h-[300px] md:min-h-0" data-id="element-139">
                  <img src="https://cdn.magicpatterns.com/uploads/dmpgepxhc8KHwcM4zavwLW/Screenshot_2026-04-15_at_19.20.27.png" alt="Prepare for your visit — Discussion Guide" className="w-full h-full object-cover object-top" data-id="element-140" />
                  {/* Overlay with Snak indicator */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" data-id="element-141" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between" data-id="element-142">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg flex items-center gap-2" data-id="element-143">
                      <motion.div animate={{
                      scale: [1, 1.1, 1]
                    }} transition={{
                      repeat: Infinity,
                      duration: 2
                    }} className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center" data-id="element-144">
                        <Play size={14} className="text-white ml-0.5" fill="white" data-id="element-145" />
                      </motion.div>
                      <span className="text-sm font-semibold text-slate-900" data-id="element-146">
                        Play as Snak
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}